import React, { useEffect, useState } from 'react';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Close as CloseIcon,
    People as PeopleIcon,
    Download as DownloadIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import AdminLayout from '../AdminLayout';
import competitionService from '../../services/competitionService';
import startupService from '../../services/startupService';
import * as XLSX from 'xlsx';

export default function CompetitionTable() {
    const [competitions, setCompetitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [openTeamDialog, setOpenTeamDialog] = useState(false);
    const [openMemberDialog, setOpenMemberDialog] = useState(false);

    // Edit states
    const [editingCompId, setEditingCompId] = useState(null);
    const [editingTeamId, setEditingTeamId] = useState(null);
    const [selectedCompetition, setSelectedCompetition] = useState(null);

    // Form data
    const [allStartups, setAllStartups] = useState([]);
    const [competitionForm, setCompetitionForm] = useState({
        title: '',
        description: '',
        lastDateToApply: '',
        resultDeclarationDate: '',
        startupIds: [],
    });

    const [teamForm, setTeamForm] = useState({
        teamName: '',
        members: [],
    });

    const [memberForm, setMemberForm] = useState({
        name: '',
        email: '',
        mobileNo: '',
        course: '',
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [comps, startups] = await Promise.all([
                competitionService.getCompetitions(),
                startupService.getStartups(),
            ]);
            setCompetitions(comps);
            setAllStartups(startups);
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ==================== COMPETITION CRUD ====================

    const handleCreateCompetition = () => {
        setEditingCompId(null);
        setCompetitionForm({
            title: '',
            description: '',
            lastDateToApply: '',
            resultDeclarationDate: '',
            startupIds: [],
        });
        setOpenDialog(true);
    };

    const handleEditCompetition = (comp) => {
        setEditingCompId(comp.id);
        setCompetitionForm(comp);
        setOpenDialog(true);
    };

    const handleSaveCompetition = async () => {
        try {
            if (!competitionForm.title.trim()) {
                setError('Competition title is required');
                return;
            }
            if (!competitionForm.startupIds || competitionForm.startupIds.length === 0) {
                setError('Please select at least one startup');
                return;
            }

            setLoading(true);

            if (editingCompId) {
                await competitionService.updateCompetition(editingCompId, competitionForm);
                setCompetitions(competitions.map(c =>
                    c.id === editingCompId ? { id: editingCompId, ...competitionForm } : c
                ));
            } else {
                const newComp = await competitionService.createCompetition(competitionForm);
                setCompetitions([...competitions, newComp]);
            }

            setOpenDialog(false);
            setError('');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCompetition = async (compId) => {
        if (!window.confirm('Delete this competition?')) return;

        try {
            setLoading(true);
            await competitionService.deleteCompetition(compId);
            setCompetitions(competitions.filter(c => c.id !== compId));
            setError('');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ==================== TEAM CRUD ====================

    const handleAddTeam = (comp) => {
        setSelectedCompetition(comp);
        setEditingTeamId(null);
        setTeamForm({ teamName: '', members: [] });
        setOpenTeamDialog(true);
    };

    const handleEditTeam = (comp, team) => {
        setSelectedCompetition(comp);
        setEditingTeamId(team.id);
        setTeamForm({ ...team }); // ✅ FIX: Deep copy team
        setOpenTeamDialog(true);
    };

    const handleSaveTeam = async () => {
        try {
            if (!teamForm.teamName.trim()) {
                setError('Team name is required');
                return;
            }

            setLoading(true);

            const teamData = {
                teamName: teamForm.teamName.trim(),
                members: teamForm.members || [],
            };

            if (editingTeamId) {
                // ✅ FIX: Update team
                await competitionService.updateCompetitionTeam(
                    selectedCompetition.id,
                    editingTeamId,
                    teamData
                );
                // console.log('✅ Team updated');
            } else {
                // ✅ FIX: Create team
                await competitionService.addCompetitionTeam(
                    selectedCompetition.id,
                    teamData
                );
                // console.log('✅ Team created');
            }

            // ✅ FIX: Refresh data
            const updated = await competitionService.getCompetitions();
            setCompetitions(updated);

            setOpenTeamDialog(false);
            setTeamForm({ teamName: '', members: [] });
            setError('');
            setSelectedCompetition(null);
        } catch (err) {
            console.error('Error saving team:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTeam = async (compId, teamId) => {
        if (!window.confirm('Delete this team?')) return;

        try {
            setLoading(true);
            await competitionService.deleteCompetitionTeam(compId, teamId);
            const updated = await competitionService.getCompetitions();
            setCompetitions(updated);
            setError('');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ==================== MEMBER CRUD ====================

    const handleAddMember = () => {
        // console.log('Opening member dialog');
        setMemberForm({ name: '', email: '', mobileNo: '', course: '' });
        setOpenMemberDialog(true);
    };

    const handleSaveMember = () => {
        try {
            if (!memberForm.name.trim()) {
                setError('Member name is required');
                return;
            }
            if (!memberForm.email.trim()) {
                setError('Email is required');
                return;
            }
            if (!memberForm.mobileNo.trim()) {
                setError('Mobile number is required');
                return;
            }
            if (!memberForm.course.trim()) {
                setError('Course is required');
                return;
            }

            // console.log('Adding member:', memberForm);

            const newMember = {
                id: Date.now().toString(),
                name: memberForm.name.trim(),
                email: memberForm.email.trim(),
                mobileNo: memberForm.mobileNo.trim(),
                course: memberForm.course.trim(),
            };

            // ✅ FIX: Update teamForm with new member
            setTeamForm(prev => {
                const updated = {
                    ...prev,
                    members: [...(prev.members || []), newMember],
                };
                // console.log('Updated team form:', updated);
                return updated;
            });

            setOpenMemberDialog(false);
            setMemberForm({ name: '', email: '', mobileNo: '', course: '' });
            setError('');
        } catch (err) {
            console.error('Error saving member:', err);
            setError(err.message);
        }
    };

    const handleDeleteMember = (memberId) => {
        setTeamForm(prev => ({
            ...prev,
            members: (prev.members || []).filter(m => m.id !== memberId),
        }));
    };

    // ==================== EXCEL EXPORT ====================

    const handleDownloadExcel = async (comp) => {
        try {
            const workbook = XLSX.utils.book_new();

            // Sheet 1: Competition Details
            const competitionData = [
                ['Competition Details'],
                ['Title', comp.title],
                ['Description', comp.description || 'N/A'],
                ['Application Deadline', comp.lastDateToApply || 'N/A'],
                ['Result Date', comp.resultDeclarationDate || 'N/A'],
                ['Status', comp.status || 'Ongoing'],
                ['Startups Applying', (comp.startupIds || []).map(id => getStartupName(id)).join(', ')],
                [],
            ];

            const competitionSheet = XLSX.utils.aoa_to_sheet(competitionData);
            XLSX.utils.book_append_sheet(workbook, competitionSheet, 'Competition');

            // Sheet 2: Teams & Members
            if (comp.teams && comp.teams.length > 0) {
                const teamData = [
                    ['Team Name', 'Member Name', 'Email', 'Mobile No', 'Course'],
                ];

                comp.teams.forEach(team => {
                    if (team.members && team.members.length > 0) {
                        team.members.forEach((member, idx) => {
                            teamData.push([
                                idx === 0 ? team.teamName : '',
                                member.name,
                                member.email,
                                member.mobileNo,
                                member.course,
                            ]);
                        });
                    } else {
                        teamData.push([team.teamName, '', '', '', '']);
                    }
                });

                const teamSheet = XLSX.utils.aoa_to_sheet(teamData);
                teamSheet['!cols'] = [
                    { wch: 20 },
                    { wch: 20 },
                    { wch: 25 },
                    { wch: 15 },
                    { wch: 15 },
                ];
                XLSX.utils.book_append_sheet(workbook, teamSheet, 'Teams & Members');
            }

            XLSX.writeFile(workbook, `${comp.title}_${Date.now()}.xlsx`);
        } catch (err) {
            console.error('Error exporting:', err);
            setError('Failed to export Excel');
        }
    };

    // ==================== HELPERS ====================

    const getStartupName = (id) => {
        return allStartups.find(s => s.id === id)?.name || 'Unknown';
    };

    const handleCompetitionChange = (e) => {
        const { name, value } = e.target;
        setCompetitionForm(prev => ({ ...prev, [name]: value }));
    };

    const handleStartupToggle = (startupId) => {
        setCompetitionForm(prev => {
            const ids = prev.startupIds || [];
            if (ids.includes(startupId)) {
                return { ...prev, startupIds: ids.filter(id => id !== startupId) };
            } else {
                return { ...prev, startupIds: [...ids, startupId] };
            }
        });
    };

    const filteredCompetitions = competitions.filter(comp =>
        comp.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ==================== RENDER ====================

    return (
        <AdminLayout title="🏆 Competitions">
            {/* ✅ FIX: Only content scrolls, not sidebar/navbar */}
            <div className="h-[calc(100vh-80px)] overflow-y-auto bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-4xl font-bold text-gray-900">🏆 Competitions</h2>
                            <p className="text-gray-600 mt-1">Manage all competitions and teams</p>
                        </div>
                        <button
                            onClick={handleCreateCompetition}
                            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold shadow-lg"
                        >
                            <AddIcon /> New Competition
                        </button>
                    </div>

                    {/* Search */}
                    <input
                        type="text"
                        placeholder="Search competitions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-md"
                    />

                    {/* Error */}
                    {error && (
                        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between">
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="font-bold text-lg">×</button>
                        </div>
                    )}

                    {/* Loading */}
                    {loading && competitions.length === 0 ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : filteredCompetitions.length === 0 ? (
                        <div className="p-12 text-center bg-white rounded-xl text-gray-500 shadow-lg">
                            No competitions found. Create one to get started!
                        </div>
                    ) : (
                        /* Competitions List */
                        <div className="space-y-6">
                            {filteredCompetitions.map(comp => (
                                <div key={comp.id} className="bg-white rounded-xl shadow-lg border-l-4 border-red-600 p-6 hover:shadow-xl transition">
                                    {/* Competition Header */}
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1">
                                            <h3 className="text-2xl font-bold text-gray-900">{comp.title}</h3>
                                            <p className="text-gray-600 mt-1">{comp.description}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleDownloadExcel(comp)}
                                                className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition"
                                                title="Download Excel"
                                            >
                                                <DownloadIcon className="text-2xl" />
                                            </button>
                                            <button
                                                onClick={() => handleEditCompetition(comp)}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                                            >
                                                <EditIcon className="text-2xl" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCompetition(comp.id)}
                                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                                            >
                                                <DeleteIcon className="text-2xl" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Competition Info */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-gray-50 p-4 rounded-lg">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-600">Apply By</p>
                                            <p className="text-gray-900 font-bold">{comp.lastDateToApply ? new Date(comp.lastDateToApply).toLocaleDateString() : 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-600">Result Date</p>
                                            <p className="text-gray-900 font-bold">{comp.resultDeclarationDate ? new Date(comp.resultDeclarationDate).toLocaleDateString() : 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-600">Status</p>
                                            <p className="text-gray-900 font-bold">{comp.status || 'Ongoing'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-600">Startups</p>
                                            <p className="text-gray-900 font-bold">{comp.startupIds?.length || 0}</p>
                                        </div>
                                    </div>

                                    {/* Teams Section */}
                                    <div className="mb-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                <PeopleIcon /> Teams ({comp.teams?.length || 0})
                                            </h4>
                                            <button
                                                onClick={() => handleAddTeam(comp)}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                                            >
                                                <AddIcon className="text-lg" /> Add Team
                                            </button>
                                        </div>

                                        {/* Teams Table */}
                                        {comp.teams && comp.teams.length > 0 ? (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-blue-100 border-b-2 border-blue-300">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left font-semibold text-blue-900">Team Name</th>
                                                            <th className="px-4 py-2 text-left font-semibold text-blue-900">Members</th>
                                                            <th className="px-4 py-2 text-center font-semibold text-blue-900">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {comp.teams.map((team, idx) => (
                                                            <tr key={team.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'}>
                                                                <td className="px-4 py-3 font-semibold text-gray-900">{team.teamName}</td>
                                                                <td className="px-4 py-3">
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {team.members && team.members.length > 0 ? (
                                                                            team.members.map(member => (
                                                                                <span key={member.id} className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold">
                                                                                    {member.name}
                                                                                </span>
                                                                            ))
                                                                        ) : (
                                                                            <span className="text-gray-500 italic">No members</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <div className="flex justify-center gap-2">
                                                                        <button
                                                                            onClick={() => handleEditTeam(comp, team)}
                                                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                                                                        >
                                                                            <EditIcon className="text-lg" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDeleteTeam(comp.id, team.id)}
                                                                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                                                                        >
                                                                            <DeleteIcon className="text-lg" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="p-4 bg-blue-50 rounded-lg text-center text-gray-500 border border-blue-200">
                                                No teams yet. Add your first team!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ==================== DIALOGS ==================== */}

                    {/* Competition Dialog */}
                    {openDialog && (
                        <CompetitionDialog
                            competition={competitionForm}
                            allStartups={allStartups}
                            isEditing={!!editingCompId}
                            onSave={handleSaveCompetition}
                            onClose={() => setOpenDialog(false)}
                            onChange={handleCompetitionChange}
                            onStartupToggle={handleStartupToggle}
                            loading={loading}
                        />
                    )}

                    {/* Team Dialog */}
                    {openTeamDialog && (
                        <TeamDialog
                            team={teamForm}
                            isEditing={!!editingTeamId}
                            setTeamForm={setTeamForm}
                            onSave={handleSaveTeam}
                            onClose={() => {
                                setOpenTeamDialog(false);
                                setTeamForm({ teamName: '', members: [] });
                                setSelectedCompetition(null);
                            }}
                            onAddMember={handleAddMember}
                            onDeleteMember={handleDeleteMember}
                            openMemberDialog={openMemberDialog}
                            memberForm={memberForm}
                            onMemberChange={(e) => {
                                const { name, value } = e.target;
                                setMemberForm(prev => ({ ...prev, [name]: value }));
                            }}
                            onSaveMember={handleSaveMember}
                            onCloseMemberDialog={() => setOpenMemberDialog(false)}
                            loading={loading}
                        />
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}

// ==================== COMPETITION DIALOG COMPONENT ====================

function CompetitionDialog({
    competition,
    allStartups,
    isEditing,
    onSave,
    onClose,
    onChange,
    onStartupToggle,
    loading,
}) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-8 max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {isEditing ? 'Edit Competition' : 'Create Competition'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <CloseIcon className="text-2xl" />
                    </button>
                </div>

                <div className="space-y-4">
                    <input
                        type="text"
                        name="title"
                        placeholder="Competition Title"
                        value={competition.title}
                        onChange={onChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />

                    <textarea
                        name="description"
                        placeholder="Description"
                        value={competition.description}
                        onChange={onChange}
                        rows="2"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Competition Webpage Link
                        </label>
                        <input
                            type="url"
                            name="competitionLink"
                            value={competition.competitionLink}
                            onChange={onChange}
                            placeholder="https://example.com/competition-details"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            last Date to Apply
                        </label>
                        <input
                            type="date"
                            name="lastDateToApply"
                            value={competition.lastDateToApply}
                            onChange={onChange}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Result Declaration Date
                        </label>
                        <input
                            type="date"
                            name="resultDeclarationDate"
                            value={competition.resultDeclarationDate}
                            onChange={onChange}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Select Startups ({(competition.startupIds || []).length} selected)
                        </label>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-gray-50">
                            {allStartups.map(startup => (
                                <label key={startup.id} className="flex items-center gap-3 cursor-pointer hover:bg-white p-2 rounded">
                                    <input
                                        type="checkbox"
                                        checked={(competition.startupIds || []).includes(startup.id)}
                                        onChange={() => onStartupToggle(startup.id)}
                                        className="w-4 h-4 rounded border-gray-300"
                                    />
                                    <span className="text-gray-700">{startup.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        disabled={loading}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50"
                    >
                        {loading ? '⏳ Saving...' : '✓ Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ==================== TEAM DIALOG COMPONENT ====================

function TeamDialog({
    team,
    isEditing,
    onSave,
    onClose,
    onAddMember,
    onDeleteMember,
    openMemberDialog,
    memberForm,
    onMemberChange,
    onSaveMember,
    onCloseMemberDialog,
    setTeamForm,
    loading,
}) {

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {isEditing ? 'Edit Team' : 'Create Team'}
                        </h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <CloseIcon className="text-2xl" />
                        </button>
                    </div>

                    {/* Team Name */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Team Name *</label>
                        <input
                            type="text"
                            value={team.teamName}
                            onChange={(e) =>
                                setTeamForm(prev => ({
                                    ...prev,
                                    teamName: e.target.value,
                                }))
                            }
                            placeholder="e.g., Frontend Team"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                    </div>

                    {/* Members Section */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <PersonIcon /> Team Members ({(team.members || []).length})
                            </h3>
                            <button
                                onClick={onAddMember}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold text-sm"
                            >
                                <AddIcon className="text-lg" /> Add
                            </button>
                        </div>

                        {/* Members Table */}
                        {team.members && team.members.length > 0 ? (
                            <div className="overflow-x-auto mb-6">
                                <table className="w-full text-sm">
                                    <thead className="bg-green-100 border-b-2 border-green-300 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 text-left font-semibold text-green-900">Name</th>
                                            <th className="px-4 py-2 text-left font-semibold text-green-900">Email</th>
                                            <th className="px-4 py-2 text-left font-semibold text-green-900">Mobile</th>
                                            <th className="px-4 py-2 text-left font-semibold text-green-900">Course</th>
                                            <th className="px-4 py-2 text-center font-semibold text-green-900">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {team.members.map((member, idx) => (
                                            <tr key={member.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-green-50'}>
                                                <td className="px-4 py-2 font-semibold text-gray-900 text-xs">{member.name}</td>
                                                <td className="px-4 py-2 text-gray-700 text-xs">{member.email}</td>
                                                <td className="px-4 py-2 text-gray-700 text-xs">{member.mobileNo}</td>
                                                <td className="px-4 py-2 text-gray-700 text-xs">{member.course}</td>
                                                <td className="px-4 py-2 text-center">
                                                    <button
                                                        onClick={() => onDeleteMember(member.id)}
                                                        className="p-1 text-red-600 hover:bg-red-100 rounded-lg transition"
                                                    >
                                                        <DeleteIcon className="text-lg" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-4 bg-green-50 rounded-lg text-center text-gray-500 border border-green-200 mb-6">
                                No members yet. Add your first member!
                            </div>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                onSave();
                            }}
                            disabled={loading || !team.teamName.trim()}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50"
                        >
                            {loading ? '⏳ Saving...' : '✓ Save Team'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ✅ FIX: Member Dialog - Fixed z-index and positioning */}
            {openMemberDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Add Member</h3>
                            <button onClick={onCloseMemberDialog} className="text-gray-400 hover:text-gray-600 text-2xl">
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Member Name"
                                    value={memberForm.name}
                                    onChange={onMemberChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    value={memberForm.email}
                                    onChange={onMemberChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile No *</label>
                                <input
                                    type="tel"
                                    name="mobileNo"
                                    placeholder="Mobile No"
                                    value={memberForm.mobileNo}
                                    onChange={onMemberChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Course *</label>
                                <input
                                    type="text"
                                    name="course"
                                    placeholder="Course (e.g., B.Tech)"
                                    value={memberForm.course}
                                    onChange={onMemberChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>

                        <div className="flex gap-4 mt-6">
                            <button
                                onClick={onCloseMemberDialog}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSaveMember}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold"
                            >
                                ✓ Add Member
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
